import { memo } from 'react';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

const messages = defineMessages({
  title: {
    id: 'Security.claimADomain.howItWorksTrayTitle',
    defaultMessage: 'How domain claims work',
    description: 'Title of the tray explaining how domain claims work',
  },
  closeAccessibilityLabel: {
    id: 'Security.claimADomain.howItWorksTrayCloseAccessibilityLabel',
    defaultMessage: 'Close how domain claims work tray',
    description: 'Accessibility label for the how-it-works tray close button',
  },
  dismiss: {
    id: 'Security.claimADomain.howItWorksTrayDismiss',
    defaultMessage: 'Got it',
    description: 'Dismiss button label for the how-it-works tray',
  },
  intro: {
    id: 'Security.claimADomain.howItWorksIntro',
    defaultMessage: 'Claiming a domain enables user management for all associated accounts:',
    description: 'Intro line above the list explaining how claiming a domain affects users',
  },
  inviteOnlyAccessLeadIn: {
    id: 'Security.claimADomain.inviteOnlyAccessLeadIn',
    defaultMessage: 'Invite-only access',
    description: 'Bold lead-in for the invite-only-signup bullet',
  },
  inviteOnlyAccessDescription: {
    id: 'Security.claimADomain.inviteOnlyAccessDescription',
    defaultMessage: 'New users must be invited to sign up using this domain.',
    description: 'Description for the invite-only-signup bullet',
  },
  existingUserTransitionLeadIn: {
    id: 'Security.claimADomain.existingUserTransitionLeadIn',
    defaultMessage: 'Existing user transition',
    description: 'Bold lead-in for the existing-user grace-period bullet',
  },
  existingUserTransitionDescription: {
    id: 'Security.claimADomain.existingUserTransitionDescription',
    defaultMessage:
      'Current users receive a grace period to opt in to management or switch to a personal email.',
    description: 'Description for the existing-user grace-period bullet',
  },
  emailRestrictionsLeadIn: {
    id: 'Security.claimADomain.emailRestrictionsLeadIn',
    defaultMessage: 'Email restrictions',
    description: 'Bold lead-in for the email-restrictions bullet',
  },
  emailRestrictionsDescription: {
    id: 'Security.claimADomain.emailRestrictionsDescription',
    defaultMessage: 'Users can no longer move their accounts into or out of this domain.',
    description: 'Description for the email-restrictions bullet',
  },
  enforcedSecurityLeadIn: {
    id: 'Security.claimADomain.enforcedSecurityLeadIn',
    defaultMessage: 'Enforced security',
    description: 'Bold lead-in for the enforced-security bullet',
  },
  enforcedSecurityDescription: {
    id: 'Security.claimADomain.enforcedSecurityDescription',
    defaultMessage: "Your organization's 2FA, login, and session settings will apply to everyone.",
    description: 'Description for the enforced-security bullet',
  },
});

const HOW_IT_WORKS_POINTS = [
  { leadIn: messages.inviteOnlyAccessLeadIn, description: messages.inviteOnlyAccessDescription },
  {
    leadIn: messages.existingUserTransitionLeadIn,
    description: messages.existingUserTransitionDescription,
  },
  { leadIn: messages.emailRestrictionsLeadIn, description: messages.emailRestrictionsDescription },
  { leadIn: messages.enforcedSecurityLeadIn, description: messages.enforcedSecurityDescription },
];

export type HowItWorksTrayProps = {
  onClose: () => void;
};

const TRAY_STYLES = { container: { width: '532px' } } as const;

// Tray's own animated close (via a ref's imperative `close()`, or its
// built-in ESC/backdrop-dismiss) relies on an internal framer-motion promise
// that has proven unreliable here — it silently no-ops instead of ever
// calling onCloseComplete, leaving the tray stuck open. Calling onClose
// directly sidesteps that mechanism entirely: no exit animation, but the
// tray is guaranteed to actually close.
export const HowItWorksTray = memo(function HowItWorksTray({ onClose }: HowItWorksTrayProps) {
  const { formatMessage } = useIntl();
  const title = formatMessage(messages.title);

  return (
    <Tray
      accessibilityLabel={title}
      hideCloseButton
      preventDismiss
      onCloseComplete={onClose}
      pin="right"
      styles={TRAY_STYLES}
      title={
        <HStack width="100%" justifyContent="flex-end">
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={onClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
            data-dd-action-name="security_how_it_works_close"
          />
        </HStack>
      }
      footer={
        <PageFooter
          borderedTop
          height="auto"
          justifyContent="flex-end"
          action={
            <Button
              variant="primary"
              compact
              onClick={onClose}
              data-dd-action-name="security_how_it_works_dismiss"
              testID="how-it-works-tray-dismiss-button"
            >
              {formatMessage(messages.dismiss)}
            </Button>
          }
        />
      }
    >
      <VStack width="100%" gap={4}>
        <VStack gap={2}>
          <Box
            width={40}
            height={40}
            borderRadius={400}
            background="bgPrimaryWash"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name="info" color="fgPrimary" size="m" />
          </Box>
          <Text as="p" font="title3" color="fg">
            {title}
          </Text>
        </VStack>
        <Text font="body" color="fgMuted">
          {formatMessage(messages.intro)}
        </Text>
        <VStack as="ul" gap={3}>
          {HOW_IT_WORKS_POINTS.map((point) => (
            <HStack key={point.leadIn.id} as="li" gap={2} alignItems="flex-start">
              <Text font="body" color="fgMuted">
                •
              </Text>
              <Text font="body" color="fgMuted">
                <Text as="strong" font="body" color="fg">
                  {formatMessage(point.leadIn)}
                </Text>
                {': '}
                {formatMessage(point.description)}
              </Text>
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Tray>
  );
});
